import React from 'react';
import { pick } from 'lodash';
import { QueryResult, MutationFn } from 'react-apollo';
import { HoCQueryComponent, HoCMutationComponent } from 'types/typescript/util';

import GET_CONTENT_SOURCE_PROVIDER_QUERY from '../queries/ContentSourceProviderQuery.gql';
import UPDATE_CONTENT_SOURCES_PROVIDER_MUTATION from '../queries/UpdateContentSourcesProviderMutation.gql';

import {
  UpdateContentSourceProvider,
  UpdateContentSourceProviderVariables,
} from '../queries/__generated__/UpdateContentSourceProvider';
import {
  ContentSourceProviderContainer as ContentSourceProviderContainerQuery,
  ContentSourceProviderContainerVariables,
} from '../queries/__generated__/ContentSourceProviderContainer';

type IncomingProps = {
  uuid: string;
  mutate: MutationFn<UpdateContentSourceProvider, UpdateContentSourceProviderVariables>;
  children: any;
};

type Props = {
  apolloProps: QueryResult<
    ContentSourceProviderContainerQuery,
    ContentSourceProviderContainerVariables
  >;
};

type variablesType = NonNullable<UpdateContentSourceProviderVariables['input']>;

type variableKeys = keyof variablesType;

const UPDATABLE: variableKeys[] = [
  'title',
  'providerIconUrl',
  'organizationUUID',
  'contentWebhookUrl',
  'enabledByProvider',
  'bigPreviewWebhookUrl',
  'productLongDescription',
  'productShortDescription',
  'providerIconFileUUID',
];

const DEFAULTS: UpdateContentSourceProviderVariables['input'] = {
  title: '',
  providerIconUrl: '',
  contentWebhookUrl: '',
  enabledByProvider: false,
  bigPreviewWebhookUrl: '',
  productLongDescription: '',
  productShortDescription: '',
  organizationUUID: '',
  providerIconFileUUID: '',
};

class ContentSourceProviderContainer extends React.Component<Props & IncomingProps, {}> {
  getContentSourceProvider = () => {
    return this.props.apolloProps.data?.contentSourceProvider;
  };

  update = (attributes: any) => {
    const mutationInput = pick(
      {
        ...DEFAULTS,
        ...(this.getContentSourceProvider() || {}),
        ...attributes,
        organizationUUID: this.props.uuid,
      },
      UPDATABLE
    );

    // we originally used to just upload the URL for the icon,
    // we have since moved to using the UUID as the urls are now signed urls via s3
    delete mutationInput.providerIconUrl;

    return this.props
      .mutate({
        variables: {
          input: mutationInput,
        },
      })
      .then(result => {
        if (!result) {
          throw new Error('missing result in UpdateContentSourceProvider mutation');
        }

        const error = result.data?.updateContentSourceProvider.error?.message;

        if (error) {
          throw new Error(error);
        } else {
          return result;
        }
      });
  };

  render() {
    return this.props.children({
      contentSourceProvider: this.getContentSourceProvider(),
      loading: this.props.apolloProps.loading,
      update: this.update,
    });
  }
}
const withTheContentSourceProviderMutation = HoCMutationComponent<
  UpdateContentSourceProvider,
  UpdateContentSourceProviderVariables,
  IncomingProps
>({ mutation: UPDATE_CONTENT_SOURCES_PROVIDER_MUTATION }, 'mutate');

const withTheContentSourceProvider = HoCQueryComponent<
  ContentSourceProviderContainerQuery,
  ContentSourceProviderContainerVariables,
  IncomingProps
>({
  query: GET_CONTENT_SOURCE_PROVIDER_QUERY,
  fetchPolicy: 'network-only',
  variablePropKeys: [{ varName: 'uuid', propName: 'uuid' }],
});

export { ContentSourceProviderContainer };
export default withTheContentSourceProviderMutation(
  withTheContentSourceProvider(ContentSourceProviderContainer)
);
